import type { ConsentFormSnapshot } from '@shared/hooks/useCarePlanPopulated'
import type React from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { commonPatterns } from 'utils/enhancedTestIds'
import ConsentFormItem, { type ConsentFormData } from './ConsentFormItem'

interface ConsentFormsListProps {
  items: ConsentFormData[]
  residentId: string
  testId?: string
  isSnapshot?: boolean
  snapshotData?: ConsentFormSnapshot[]
  disableItemNavigation?: boolean
}

const ConsentFormsList: React.FC<ConsentFormsListProps> = ({
  items,
  residentId,
  testId,
  isSnapshot = false,
  snapshotData = [],
  disableItemNavigation = false
}) => {
  const navigate = useNavigate()

  if (!items?.length) {
    return null
  }

  const handleItemClick = (consentId: string) => {
    // Scroll the main content container to top
    const container = document.querySelector(
      '[data-testid="residents-content-container"]'
    )
    if (container) {
      container.scrollTo(0, 0)
    }
    if (isSnapshot) {
      // For historical snapshots, navigate with snapshot data in state
      const snapshot = snapshotData.find((s) => s.id === consentId)
      if (snapshot) {
        navigate(`/residents/${residentId}/consent/${consentId}`, {
          state: { snapshot, isSnapshot: true, from: 'carePlan' }
        })
      }
    } else {
      // For active care plans, navigate to the consent form detail page
      navigate(`/residents/${residentId}/consent/${consentId}`, {
        state: { from: 'carePlan' }
      })
    }
  }

  return (
    <Wrapper data-testid={testId}>
      <ItemsWrapper>
        {items.map((item, index) => (
          <ConsentFormItem
            key={item.id}
            data={item}
            testId={testId ? commonPatterns.row(testId, index) : undefined}
            onClick={
              disableItemNavigation ? undefined : () => handleItemClick(item.id)
            }
          />
        ))}
      </ItemsWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export default ConsentFormsList
